import { create } from 'zustand'

/**
 * Tracks real-time ticket update metadata received via SignalR.
 *
 * Each entry records a tag ("New" or "Updated") and the lastActivityDate
 * that triggered it. The source-of-truth rule: a new event only overwrites
 * an existing entry if its lastActivityDate is >= the stored value.
 *
 * This store is separate from React Query — it holds ephemeral UI badge state,
 * not server data. It resets naturally on page reload (company switch).
 */
const useLiveUpdateStore = create((set, get) => ({
    // { [ticketId]: { tag, lastActivityDate, noteIds: [], updatedNoteIds: [], attachmentIds: [] } }
    updates: {},

    // { [ticketId]: { [attachmentId]: { id, title, fullPath, fileSize, ... } } }
    recentAttachments: {},

    // { [ticketId]: { id, ticketNumber, title, description, ... } }
    recentTickets: {},

    // { [ticketId]: { [noteId]: { id, ticketID, title, description, ... } } }
    recentNotes: {},

    markTicketNew: (ticketId, lastActivityDate) => {
        const current = get().updates[ticketId]
        if (current && current.lastActivityDate > lastActivityDate) return

        set((state) => {
            const existing = state.updates[ticketId]
            return {
                updates: {
                    ...state.updates,
                    [ticketId]: {
                        ...existing,
                        tag: 'New',
                        lastActivityDate,
                        noteIds: existing?.noteIds || [],
                        updatedNoteIds: existing?.updatedNoteIds || [],
                        attachmentIds: existing?.attachmentIds || [],
                    }
                }
            }
        })
    },

    markTicketUpdated: (ticketId, lastActivityDate) => {
        const current = get().updates[ticketId]
        if (current && current.lastActivityDate > lastActivityDate) return

        set((state) => {
            const existing = state.updates[ticketId]
            return {
                updates: {
                    ...state.updates,
                    [ticketId]: {
                        ...existing,
                        tag: 'Updated',
                        lastActivityDate,
                        noteIds: existing?.noteIds || [],
                        updatedNoteIds: existing?.updatedNoteIds || [],
                        attachmentIds: existing?.attachmentIds || [],
                    }
                }
            }
        })
    },

    markNoteNew: (ticketId, noteId, lastActivityDate) => {
        set((state) => {
            const existing = state.updates[ticketId] || { tag: null, lastActivityDate: '', noteIds: [], updatedNoteIds: [], attachmentIds: [] }
            const noteIds = existing.noteIds.includes(noteId)
                ? existing.noteIds
                : [...existing.noteIds, noteId]
            return {
                updates: {
                    ...state.updates,
                    [ticketId]: {
                        ...existing,
                        tag: existing.tag || 'Updated',
                        lastActivityDate: lastActivityDate > existing.lastActivityDate ? lastActivityDate : existing.lastActivityDate,
                        noteIds,
                    }
                }
            }
        })
    },

    markNoteUpdated: (ticketId, noteId, lastActivityDate) => {
        set((state) => {
            const existing = state.updates[ticketId] || { tag: null, lastActivityDate: '', noteIds: [], updatedNoteIds: [], attachmentIds: [] }
            const updatedNoteIds = existing.updatedNoteIds.includes(noteId)
                ? existing.updatedNoteIds
                : [...existing.updatedNoteIds, noteId]
            return {
                updates: {
                    ...state.updates,
                    [ticketId]: {
                        ...existing,
                        tag: existing.tag || 'Updated',
                        lastActivityDate: lastActivityDate > existing.lastActivityDate ? lastActivityDate : existing.lastActivityDate,
                        updatedNoteIds,
                    }
                }
            }
        })
    },

    markAttachmentNew: (ticketId, attachmentId, lastActivityDate) => {
        const normalizedId = Number(attachmentId)
        const safeAttachmentId = Number.isFinite(normalizedId) ? normalizedId : String(attachmentId)

        set((state) => {
            const existing = state.updates[ticketId] || { tag: null, lastActivityDate: '', noteIds: [], updatedNoteIds: [], attachmentIds: [] }
            const alreadyPresent = (existing.attachmentIds || []).some((id) => String(id) === String(safeAttachmentId))
            const attachmentIds = alreadyPresent
                ? existing.attachmentIds
                : [...existing.attachmentIds, safeAttachmentId]
            return {
                updates: {
                    ...state.updates,
                    [ticketId]: {
                        ...existing,
                        tag: existing.tag || 'Updated',
                        lastActivityDate: lastActivityDate > existing.lastActivityDate ? lastActivityDate : existing.lastActivityDate,
                        attachmentIds,
                    }
                }
            }
        })
    },

    clearTicket: (ticketId) => {
        set((state) => {
            const { [ticketId]: _, ...rest } = state.updates
            return { updates: rest }
        })
    },

    clearAll: () => {
        set({ updates: {}, recentAttachments: {}, recentTickets: {}, recentNotes: {} })
    },

    getTag: (ticketId) => {
        return get().updates[ticketId]?.tag || null
    },

    isNoteFlagged: (ticketId, noteId) => {
        const entry = get().updates[ticketId]
        if (!entry) return false
        const noteIdStr = String(noteId)
        return (entry.noteIds || []).some((id) => String(id) === noteIdStr) || (entry.updatedNoteIds || []).some((id) => String(id) === noteIdStr)
    },

    isAttachmentFlagged: (ticketId, attachmentId) => {
        const entry = get().updates[ticketId]
        if (!entry) return false
        const attachmentIdStr = String(attachmentId)
        return (entry.attachmentIds || []).some((id) => String(id) === attachmentIdStr)
    },

    clearAttachmentFlag: (ticketId, attachmentId) => {
        set((state) => {
            const existing = state.updates[ticketId]
            if (!existing) return state

            const attachmentIdStr = String(attachmentId)
            const nextAttachmentIds = (existing.attachmentIds || []).filter((id) => String(id) !== attachmentIdStr)

            return {
                updates: {
                    ...state.updates,
                    [ticketId]: {
                        ...existing,
                        attachmentIds: nextAttachmentIds,
                    }
                }
            }
        })
    },

    addRecentAttachment: (ticketId, attachment) => {
        set((state) => ({
            recentAttachments: {
                ...state.recentAttachments,
                [ticketId]: {
                    ...state.recentAttachments[ticketId],
                    [attachment.id]: attachment,
                }
            }
        }))
    },

    removeRecentAttachment: (ticketId, attachmentId) => {
        set((state) => {
            const ticketAttachments = { ...state.recentAttachments[ticketId] }
            delete ticketAttachments[attachmentId]
            const hasRemaining = Object.keys(ticketAttachments).length > 0
            const recentAttachments = { ...state.recentAttachments }
            if (hasRemaining) {
                recentAttachments[ticketId] = ticketAttachments
            } else {
                delete recentAttachments[ticketId]
            }
            return { recentAttachments }
        })
    },

    removeRecentAttachments: (ticketId, attachmentIds) => {
        set((state) => {
            const ticketAttachments = { ...state.recentAttachments[ticketId] }
            attachmentIds.forEach((id) => delete ticketAttachments[id])
            const hasRemaining = Object.keys(ticketAttachments).length > 0
            const recentAttachments = { ...state.recentAttachments }
            if (hasRemaining) {
                recentAttachments[ticketId] = ticketAttachments
            } else {
                delete recentAttachments[ticketId]
            }
            return { recentAttachments }
        })
    },

    getRecentAttachments: (ticketId) => {
        const ticketAttachments = get().recentAttachments[ticketId]
        return ticketAttachments ? Object.values(ticketAttachments) : []
    },

    addRecentTicket: (ticketId, ticket) => {
        set((state) => ({
            recentTickets: {
                ...state.recentTickets,
                [ticketId]: ticket,
            }
        }))
    },

    removeRecentTicket: (ticketId) => {
        set((state) => {
            const recentTickets = { ...state.recentTickets }
            delete recentTickets[ticketId]
            return { recentTickets }
        })
    },

    removeRecentTickets: (ticketIds) => {
        set((state) => {
            const recentTickets = { ...state.recentTickets }
            ticketIds.forEach((id) => delete recentTickets[id])
            return { recentTickets }
        })
    },

    getRecentTickets: () => {
        return Object.values(get().recentTickets)
    },

    addRecentNote: (ticketId, note) => {
        set((state) => ({
            recentNotes: {
                ...state.recentNotes,
                [ticketId]: {
                    ...state.recentNotes[ticketId],
                    [note.id]: note,
                }
            }
        }))
    },

    removeRecentNote: (ticketId, noteId) => {
        set((state) => {
            const ticketNotes = { ...state.recentNotes[ticketId] }
            delete ticketNotes[noteId]
            const hasRemaining = Object.keys(ticketNotes).length > 0
            const recentNotes = { ...state.recentNotes }
            if (hasRemaining) {
                recentNotes[ticketId] = ticketNotes
            } else {
                delete recentNotes[ticketId]
            }
            return { recentNotes }
        })
    },

    removeRecentNotes: (ticketId, noteIds) => {
        set((state) => {
            const ticketNotes = { ...state.recentNotes[ticketId] }
            noteIds.forEach((id) => delete ticketNotes[id])
            const hasRemaining = Object.keys(ticketNotes).length > 0
            const recentNotes = { ...state.recentNotes }
            if (hasRemaining) {
                recentNotes[ticketId] = ticketNotes
            } else {
                delete recentNotes[ticketId]
            }
            return { recentNotes }
        })
    },

    getRecentNotes: (ticketId) => {
        const ticketNotes = get().recentNotes[ticketId]
        return ticketNotes ? Object.values(ticketNotes) : []
    },
}))

export default useLiveUpdateStore
